import React, { useContext, useEffect, useState } from 'react'
import iiitblogo from "../images/iiitb_logo.png";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import HelpIcon from "../images/help_chat.png";
import { LogOut, UserIcon } from 'lucide-react';
import LogoutButton from './LogoutButton';
import { LoginContext } from '../context/LoginContext';
import apiConfig from '../config/apiConfig';
import { Round } from './Resource/ViewRounds';
import { apiFetch } from '../apis/apiFetch';
import { useQuery } from '@tanstack/react-query';
import { toast } from '../utils/toast';
import moment from "moment";

interface AnalyticsCounts {
    // Mtech

    programme1: string;
    programme2: string;
    programme3: string;
    programme4: string;
    programme5: string;

    seatsavailable1: number;
    seatsavailable2: number;
    seatsavailable3: number;
    seatsavailable4: number;
    seatsavailable5: number;

    lastUpdated1: string;
    lastUpdated2: string;
    lastUpdated3: string;
    lastUpdated4: string;
    lastUpdated5: string;

}

const programmeFullNames: Record<string, string> = {
    "MTech_CSE": "MTech_CSE",
    "MTech_ECE": "MTech_ECE",
    "MTech_AI_DS": "MTech_AI&DS",
    "BTech_CSE": "B.Tech. (Computer Science and Engineering)",
    "BTech_ECE": "B.Tech. (Electronics and Communication Engineering)",
    "BTech_AI_DS": "B.Tech. (Artificial Intelligence & Data Science)",
    "I_MTech_CSE": "Integrated M.Tech (Computer Science and Engineering)",
    "I_MTech_ECE": "Integrated M.Tech. (Electronics and Communication Engineering)"
};

const getAbbreviatedProg = (prog: string | null | undefined): string => {
    if (!prog) return "NA";
    return prog
        .replace("I_MTech", "IMTech")
        .replace(/_/g, " ")
        .replace("AI DS", "AI&DS")
        .replace("AI & DS", "AI&DS");
};

function BtechOnlineRound() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useContext(LoginContext);
    const isAdmin = user.role === "admin"
    const { appId: id } = useParams();
    const [token, setToken] = useState<number | null>(null)

    const n = (val: number | undefined) => (val ?? 0);

    const userEmail = user?.email_id || "Student";
    const [analytics, setAnalytics] = useState<any>(null);
    const [analyticsLoading, setAnalyticsLoading] = useState(false);
    const [rounds, setRounds] = useState<Round[]>([]);
    const [selectedRound, setSelectedRound] = useState<Round | null>(null);
    const [drives, setDrives] = useState<any[]>([]);
    const [isConfirming, setIsConfirming] = useState(false);
    const [loading, setLoading] = useState(false)
    const [searchText, setSearchText] = useState("");
    const [hoveredBtn, setHoveredBtn] = useState<string | null>(null);
    const [confirmModal, setConfirmModal] = useState<{
        isOpen: boolean;
        programme: string;
        delta: number;
    }>({
        isOpen: false,
        programme: "",
        delta: 0,
    });

    const triggerConfirmModal = (programme: string, delta: number) => {
        if (delta < 0) {
            let currentSeats = 0;
            if (analytics) {
                if (analytics.programme1 === programme) currentSeats = n(analytics.seatsavailable1);
                else if (analytics.programme2 === programme) currentSeats = n(analytics.seatsavailable2);
                else if (analytics.programme3 === programme) currentSeats = n(analytics.seatsavailable3);
                else if (analytics.programme4 === programme) currentSeats = n(analytics.seatsavailable4);
                else if (analytics.programme5 === programme) currentSeats = n(analytics.seatsavailable5);
            }
            if (currentSeats <= 0) {
                toast.error("Seats cannot be less than 0");
                return;
            }
        }
        setConfirmModal({
            isOpen: true,
            programme,
            delta,
        });
    };

    const handleUpdateSeats = async (programme: string, delta: number) => {
        if (!selectedRound?.id) return;
        setAnalyticsLoading(true);
        try {
            const args = `round_id:${selectedRound.id},programme:${programme},delta:${delta}`;
            const url = `${apiConfig.getResourceUrl("rounds")}?queryId=UPDATE_REMAINING_SEATS&args=${encodeURIComponent(args)}`;
            const response = await apiFetch(url);
            if (response.ok) {
                toast.success(`Remaining seats updated successfully`);
                await fetchRounds();
                refetch();
            } else {
                toast.error(`Failed to update remaining seats`);
            }
        } catch (err) {
            console.error("Error updating seats", err);
            toast.error(`Error updating remaining seats`);
        } finally {
            setAnalyticsLoading(false);
        }
    };


    const fetchRounds = async () => {
        try {
            const url = `${apiConfig.getResourceUrl("rounds")}?queryId=GET_ALL`;
            const response = await apiFetch(url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const data = await response.json();
            let rawRounds = [];
            if (Array.isArray(data)) rawRounds = data;
            else if (data && Array.isArray(data.resource)) rawRounds = data.resource;
            else if (data && Array.isArray(data.records)) rawRounds = data.records;

            const sorted = rawRounds.sort((a: any, b: any) => {
                const parseDate = (val: any) => {
                    if (!val) return 0;
                    const d = new Date(val);
                    if (!isNaN(d.getTime())) return d.getTime();
                    const n = Number(val);
                    if (!isNaN(n)) return n;
                    return 0;
                };
                return parseDate(b.startdate || b.date) - parseDate(a.startdate || a.date);
            });
            setRounds(sorted);
        } catch (err) {
            console.error("ViewRounds: Error fetching rounds", err);
        }
    };
    const fetchDrives = async () => {
        try {
            const url = `${apiConfig.getResourceUrl("drive")}?queryId=GET_ALL`;
            const response = await apiFetch(url);
            if (response.ok) {
                const data = await response.json();
                let rawDrives = [];
                if (Array.isArray(data)) rawDrives = data;
                else if (data && Array.isArray(data.resource)) rawDrives = data.resource;
                else if (data && Array.isArray(data.records)) rawDrives = data.records;
                setDrives(rawDrives);
            }
        } catch (err) {
            console.error("ViewRounds: Error fetching drives", err);
        }
    };
    useEffect(() => {
        fetchRounds();
    }, [id]);

    useEffect(() => {
        fetchDrives();
    }, []);

    const { data: fetchedDataById, isLoading: loadingEditComp, refetch } = useQuery({
        queryKey: ["getById", "Application", id],
        queryFn: async () => {
            const params = new URLSearchParams({
                args: `id:${id}`,
                queryId: "GET_BY_ID",
            });

            const res = await apiFetch(`${apiConfig.getResourceUrl("applications")}?${params.toString()}`);
            if (!res.ok) throw new Error("Fetch failed");
            return res.json();
        },
        enabled: !!id,
    });

    useEffect(() => {
        const fetchedApp = fetchedDataById?.resource?.[0];
        const driveId = fetchedApp?.drive_id;
        if (!driveId || rounds.length === 0 || drives.length === 0) return;

        const drive = drives.find(d => d.id === driveId);
        const isMtech = drive?.drivetype === "Mtech";

        let targetRoundId = fetchedApp?.online_round5_offered_round_id || fetchedApp?.online_round5_id || fetchedApp?.online_round4_offered_round_id || fetchedApp?.online_round4_id || fetchedApp?.online_round3_offered_round_id || fetchedApp?.online_round3_id || fetchedApp?.online_round2_offered_round_id || fetchedApp?.online_round2_id || fetchedApp?.onlineRound;
        let round = rounds.find((r) => r.id === targetRoundId);
        
        if (!round) {
             round = rounds.find((r) => r.round_type === "Online" && r.drive_id === driveId && r.round_number === 4) 
                  || rounds.find((r) => r.round_type === "Online" && r.drive_id === driveId && r.round_number === 3) 
                  || rounds.find((r) => r.round_type === "Online" && r.drive_id === driveId && r.round_number === 2)
                  || rounds.find((r) => r.round_type === "Online" && r.drive_id === driveId);
        }

        if (round) {
            setSelectedRound(round);
            if (isMtech) {
                setAnalytics({
                    programme1: "MTech_CSE",
                    programme2: "MTech_ECE",
                    programme3: "MTech_AI_DS",
                    programme4: "",
                    programme5: "",
                    seatsavailable1: round?.mtech_online_cse_remaining ?? round?.mtech_online_cse_seats ?? 0,
                    seatsavailable2: round?.mtech_online_ece_remaining ?? round?.mtech_online_ece_seats ?? 0,
                    seatsavailable3: round?.mtech_online_aids_remaining ?? round?.mtech_online_aids_seats ?? 0,
                    seatsavailable4: 0,
                    seatsavailable5: 0,
                    lastUpdated1: round?.mtech_online_cse_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated2: round?.mtech_online_ece_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated3: round?.mtech_online_aids_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated4: "",
                    lastUpdated5: "",
                });
            } else {
                setAnalytics({
                    programme1: "BTech_CSE",
                    programme2: "BTech_ECE",
                    programme3: "BTech_AI_DS",
                    programme4: "I_MTech_CSE",
                    programme5: "I_MTech_ECE",
                    seatsavailable1: round?.btech_online_cse_remaining ?? round?.btech_online_cse_seats ?? 0,
                    seatsavailable2: round?.btech_online_ece_remaining ?? round?.btech_online_ece_seats ?? 0,
                    seatsavailable3: round?.btech_online_aids_remaining ?? round?.btech_online_aids_seats ?? 0,
                    seatsavailable4: round?.imtech_online_cse_remaining ?? round?.imtech_online_cse_seats ?? 0,
                    seatsavailable5: round?.imtech_online_ece_remaining ?? round?.imtech_online_ece_seats ?? 0,
                    lastUpdated1: round?.btech_online_cse_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated2: round?.btech_online_ece_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated3: round?.btech_online_aids_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated4: round?.imtech_online_cse_last_updated ?? round?.last_updated_at ?? "",
                    lastUpdated5: round?.imtech_online_ece_last_updated ?? round?.last_updated_at ?? "",
                });
            }
        }
    }, [rounds, fetchedDataById, drives]);

    console.log("analytics", analytics)
    const baseUrl = apiConfig.getResourceUrl("Drive");

    // console.log("fetchedDataById", fetchedDataById?.resource?.[0]?.name)
    // console.log("fetchedDataById", fetchedDataById?.resource?.[0]?.name)

    const [localAccepted, setLocalAccepted] = useState(false);
    const [confirmModalData, setConfirmModalData] = useState<{ isOpen: boolean; programme: string }>({ isOpen: false, programme: "" });
    const [invitedProgrammes, setInvitedProgrammes] = useState<string[]>([]);

    useEffect(() => {
        setLocalAccepted(false);
        const fetchInvited = async () => {
            if (id && selectedRound?.id) {
                try {
                    const args = `application_id:${id},round_id:${selectedRound.id}`;
                    const url = `${apiConfig.getResourceUrl("applications")}?queryId=GET_INVITED_PROGRAMMES&args=${encodeURIComponent(args)}`;
                    const response = await apiFetch(url);
                    if (response.ok) {
                        const data = await response.json();
                        // API may return array directly or wrapped in {resource:[...]}
                        const arr = Array.isArray(data) ? data : (data?.resource || []);
                        const progs: string[] = arr?.[0]?.extra_data?.invited_programmes || [];
                        console.log("[InvitedProgrammes] raw:", progs);
                        setInvitedProgrammes(progs.map((p: string) => p.replace(/[^a-zA-Z0-9]/g, "").toLowerCase()));
                    }
                } catch (e) {
                    console.error("Failed to fetch invited programmes", e);
                }
            }
        };
        fetchInvited();
    }, [id, selectedRound]);

    const handleApply = async (programme: string) => {
        // Validate that candidate actually applied for this programme
        const normalizeProg = (p?: string) => p?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase() || "";
        const normProgramme = normalizeProg(programme);
        const prefs = [
            appData.pref1, appData.pref2, appData.pref3,
            appData.pref4, appData.pref5
        ].filter(Boolean).map(normalizeProg);

        // Map BTech short codes to their exact normalized preference strings
        const btechProgToPref: Record<string, string> = {
            "btechcse": "btechcomputerscienceandengineering",
            "btechece": "btechelectronicsandcommunicationengineering",
            "btechaids": "btechartificialintelligenceanddatascience",
            "imtechcse": "integratedmtechcomputerscienceandengineering",
            "imtechece": "integratedmtechelectronicsandcommunicationengineering"
        };
        const expectedPref = btechProgToPref[normProgramme] || normProgramme;

        const isMtechLocal = drives.find((d: any) => d.id === appData?.drive_id)?.drivetype === "Mtech";
        if (isMtechLocal && prefs.length > 0 && !prefs.includes(expectedPref)) {
            toast.error(`Cannot allocate seat: Candidate did not apply for ${programme.replace(/_/g, " ")}.`);
            return;
        }

        setIsConfirming(true);

        try {
            let finalProgramme = programme;
            if (isMtechLocal && programme === "MTech_AI_DS") {
                finalProgramme = "MTech AI&DS";
            }
            const args = `app_id:${id},` +
                `round_id:${selectedRound?.id},` +
                `programme:${finalProgramme},`
            const url = `${apiConfig.getResourceUrl("applications")}?queryId=ACCEPT_SEAT&args=${encodeURIComponent(args)}`;
            const response = await apiFetch(url, { method: "GET" });
            const res = await response.json();

            if (response.ok && res?.errCode === 0) {
                const results = res.resource?.[0] || [];
                console.log("allocation response", results);
                setLocalAccepted(true);
                fetchRounds();
                refetch();
                toast.success(`Seat Allocated for ${programme}`);
            } else {
                toast.error(res?.errMsg || res?.message || `Unable to Allocate seat`);
            }
        } catch (err: any) {
            console.error("Confirmation failed", err);
            toast.error(err?.message || `Unable to Allocate seat`);
        }
        finally {
            setIsConfirming(false);
            // setShowConfirmationModal(false);
        }
    };
    const appData = fetchedDataById?.resource?.[0] || {};
    const isMtech = drives.find((d: any) => d.id === fetchedDataById?.resource?.[0]?.drive_id)?.drivetype === "Mtech";
    const isOnlineRound2 = selectedRound?.round_type === "Online" && selectedRound?.round_number === 2;
    const isOnlineRound5 = selectedRound?.round_type === "Online" && selectedRound?.round_number === 5;
    const isOnlineRound4 = selectedRound?.round_type === "Online" && selectedRound?.round_number === 4;
    const isOnlineRound3 = selectedRound?.round_type === "Online" && selectedRound?.round_number === 3;
    const tokenField = isOnlineRound5 ? appData.online_round5_token : (isOnlineRound4 ? appData.online_round4_token : (isOnlineRound3 ? appData.online_round3_token : (isOnlineRound2 ? appData.online_round2_token : appData.online_round_token)));
    const iir = tokenField || appData.iir;
    const bjr = appData.bjr;
    const name = appData.fullname;
    const getOnlineField = (app: any, fieldName: string) => {
        if (isOnlineRound5) {
            if (fieldName === 'onlineRound') return app.online_round5_offered_round_id || app.online_round5_id;
            if (fieldName === 'online_round_status') return app.online_round5_status;
            if (fieldName === 'online_offer_status') return app.online_round5_offer_status;
            if (fieldName === 'online_sliding_status') return app.online_round5_sliding_status;
            if (fieldName === 'online_round_token') return app.online_round5_token;
            if (fieldName === 'online_round_allocated_programme') return app.online_round5_allocated_programme;
            if (fieldName === 'online_round_allocated_programme_2') return app.online_round5_allocated_programme_2;
            if (fieldName === 'online_round_token_2') return app.online_round5_token_2;
            if (fieldName === 'online_offer_status_2') return app.online_round5_offer_status_2;
            if (fieldName === 'online_offered_payment_status') return app.online_round5_offered_payment_status;
            if (fieldName === 'online_upgrade_notification_sent') return app.online_round5_upgrade_notification_sent;
        }
        if (isOnlineRound4) {
            if (fieldName === 'onlineRound') return app.online_round4_offered_round_id || app.online_round4_id;
            if (fieldName === 'online_round_status') return app.online_round4_status;
            if (fieldName === 'online_offer_status') return app.online_round4_offer_status;
            if (fieldName === 'online_sliding_status') return app.online_round4_sliding_status;
            if (fieldName === 'online_round_token') return app.online_round4_token;
            if (fieldName === 'online_round_allocated_programme') return app.online_round4_allocated_programme;
            if (fieldName === 'online_round_allocated_programme_2') return app.online_round4_allocated_programme_2;
            if (fieldName === 'online_round_token_2') return app.online_round4_token_2;
            if (fieldName === 'online_offer_status_2') return app.online_round4_offer_status_2;
            if (fieldName === 'online_offered_payment_status') return app.online_round4_offered_payment_status;
            if (fieldName === 'online_upgrade_notification_sent') return app.online_round4_upgrade_notification_sent;
        }
        if (isOnlineRound3) {
            if (fieldName === 'onlineRound') return app.online_round3_offered_round_id || app.online_round3_id || app.onlineRound;
            if (fieldName === 'online_round_status') return app.online_round3_status;
            if (fieldName === 'online_offer_status') return app.online_offer_status;
            if (fieldName === 'online_sliding_status') return app.online_round3_sliding_status;
            if (fieldName === 'online_round_token') return app.online_round3_token;
            if (fieldName === 'online_round_allocated_programme') return app.online_round3_allocated_programme;
            if (fieldName === 'online_round_allocated_programme_2') return app.online_round3_allocated_programme_2;
            if (fieldName === 'online_round_token_2') return app.online_round3_token_2;
            if (fieldName === 'online_offer_status_2') return app.online_round3_offer_status_2;
        }
        if (isOnlineRound2) {
            if (fieldName === 'onlineRound') return app.online_round2_offered_round_id || app.online_round2_id || app.onlineRound;
            if (fieldName === 'online_round_status') return app.online_round2_status;
            if (fieldName === 'online_offer_status') return app.online_round2_offer_status;
            if (fieldName === 'online_sliding_status') return app.online_round2_sliding_status;
            if (fieldName === 'online_round_token') return app.online_round2_token;
            if (fieldName === 'online_round_allocated_programme') return app.online_round2_allocated_programme;
            if (fieldName === 'online_round_allocated_programme_2') return app.online_round2_allocated_programme_2;
            if (fieldName === 'online_round_token_2') return app.online_round2_token_2;
            if (fieldName === 'online_offer_status_2') return app.online_round2_offer_status_2;
        }
        return app[fieldName];
    };

    const disableApply = localAccepted ||
        ["ONLINE_ALLOCATED", "OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT"].includes(getOnlineField(appData, 'online_round_status')) ||
        ["OFFER_CONFIRMED", "OFFER_GENERATED", "OFFER_SENT", "ACCEPTED"].includes(getOnlineField(appData, 'online_offer_status'));

    const isPaidSlide = appData.prev_offered_programme && appData.offered_payment_status === "SUCCESS";
    // Check if there's an active offer in the slots
    const activeSlot = [
        { p: appData.offered_programme_1, s: appData.offer_status_1 },
        { p: appData.offered_programme_2, s: appData.offer_status_2 },
        { p: appData.offered_programme_3, s: appData.offer_status_3 },
    ].find((slot) => slot.p && (slot.s === "OFFER_SENT" || slot.s === "ACCEPTED"));

    let latestProgramme = appData.online_round5_allocated_programme || appData.online_round4_allocated_programme || appData.online_round3_allocated_programme || appData.online_round2_allocated_programme || appData.online_round_allocated_programme;
    if (!latestProgramme) {
        if (isPaidSlide && activeSlot && activeSlot.p !== appData.prev_offered_programme) {
            latestProgramme = activeSlot.p;
        } else {
            if (isMtech) {
                latestProgramme = activeSlot?.p || appData.offered_programme || appData.offered_payment_programme || appData.prev_offered_programme;
            } else {
                latestProgramme = appData.offered_programme || appData.offered_payment_programme || activeSlot?.p || appData.prev_offered_programme;
            }
        }
    }

    let preRound2Programme = isOnlineRound5 ? (appData.online_round4_allocated_programme || appData.online_round3_allocated_programme || appData.online_round2_allocated_programme || appData.online_round_allocated_programme) : (isOnlineRound4 ? (appData.online_round3_allocated_programme || appData.online_round2_allocated_programme || appData.online_round_allocated_programme) : (isOnlineRound3 ? (appData.online_round2_allocated_programme || appData.online_round_allocated_programme) : (isOnlineRound2 ? appData.online_round_allocated_programme : null)));
    if (!preRound2Programme) {
        if (isPaidSlide && activeSlot && activeSlot.p !== appData.prev_offered_programme) {
            preRound2Programme = activeSlot.p;
        } else {
            if (isMtech) {
                preRound2Programme = activeSlot?.p || appData.offered_programme_1 || appData.offered_programme || appData.offered_payment_programme || appData.prev_offered_programme;
            } else {
                preRound2Programme = appData.offered_programme || appData.offered_payment_programme || activeSlot?.p || appData.offered_programme_1 || appData.prev_offered_programme;
            }
        }
    }

    let latestOfferedRoundName = "";
    if (appData.online_round5_allocated_programme) {
        latestOfferedRoundName = appData.online_round5_name || rounds.find((r: any) => r.id === appData.online_round5_offered_round_id || r.id === appData.online_round5_id)?.name || "";
    }
    if (!latestOfferedRoundName && appData.online_round4_allocated_programme) {
        latestOfferedRoundName = appData.online_round4_name || rounds.find((r: any) => r.id === appData.online_round4_offered_round_id || r.id === appData.online_round4_id)?.name || "";
    }
    if (!latestOfferedRoundName && appData.online_round3_allocated_programme) {
        latestOfferedRoundName = appData.online_round3_name || rounds.find((r: any) => r.id === appData.online_round3_offered_round_id || r.id === appData.online_round3_id)?.name || "";
    }
    if (!latestOfferedRoundName && appData.online_round2_allocated_programme) {
        latestOfferedRoundName = appData.online_round2_name || rounds.find((r: any) => r.id === appData.online_round2_offered_round_id || r.id === appData.online_round2_id)?.name || "";
    }
    if (!latestOfferedRoundName && appData.online_round_allocated_programme) {
        latestOfferedRoundName = appData.online_round_name || rounds.find((r: any) => r.id === appData.online_offered_round_id || r.id === appData.onlineRound)?.name || "";
    }
    if (!latestOfferedRoundName) {
        latestOfferedRoundName = decodeURIComponent(appData.offered_round_name || appData.prev_round_name || "");
    }


    // MTech: collect applied preferences + GATE scores (Max 3 preferences as per rules)
    const mtechPrefs = isMtech ? [
        { pref: 1, prog: appData?.pref1, score: appData?.pref1score, subjects: appData?.pref1subjects, year: appData?.pref1year, rank: appData?.pref1rank, rollno: appData?.pref1rollno },
        { pref: 2, prog: appData?.pref2, score: appData?.pref2score, subjects: appData?.pref2subjects, year: appData?.pref2year, rank: appData?.pref2rank, rollno: appData?.pref2rollno },
        { pref: 3, prog: appData?.pref3, score: appData?.pref3score, subjects: appData?.pref3subjects, year: appData?.pref3year, rank: appData?.pref3rank, rollno: appData?.pref3rollno },
    ].filter(p => p.prog) : [];

    const renderDetailSection = (title: string, details: { label: string, value: any }[], isRejected: boolean = false) => {
        const validDetails = details.filter(d => d.value !== undefined && d.value !== null && d.value !== "");
        if (validDetails.length === 0) return null;
        return (
            <div style={{ marginBottom: 20 }}>
                <h6 style={{ fontWeight: 600, borderBottom: "1px solid #e5e7eb", paddingBottom: 4, marginBottom: 8, color: isRejected ? "#dc2626" : "#374151" }}>
                    {title} {isRejected && <span style={{ fontSize: "12px", marginLeft: "4px" }}>(REJECTED)</span>}
                </h6>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
                    {validDetails.map((d, i) => (
                        <div key={i} style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>{d.label}</span>
                            <span style={{ fontSize: 14, color: isRejected ? "#dc2626" : "#111827", fontWeight: 500 }}>{d.value}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    useEffect(() => {
        if (location.state?.token) {
            setToken(location.state.token);
        } else if (iir) {
            setToken(parseInt(iir));
        }
    }, [iir, location.state?.token])

    const requestApply = (programme: string) => {
        setConfirmModalData({ isOpen: true, programme });
    };

    const getDisableApply = (programme: string) => {
        if (isMtech) {
            const normProgramme = programme.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
            if (!invitedProgrammes.includes(normProgramme)) return true;
            
            const allocated = getOnlineField(appData, 'online_round_allocated_programme');
            const allocated2 = getOnlineField(appData, 'online_round_allocated_programme_2');
            if (allocated === programme || allocated2 === programme) return true;
            return false;
        } else {
            const p1 = getOnlineField(appData, 'online_round_allocated_programme');
            if (p1) return true; // Disable all if allocated
            return false;
        }
    };

    const ApplyButton = ({
        programme
    }: {
        programme: string
    }) => {
        const disabled = getDisableApply(programme);
        return (
            <button
                className="apply-btn"
                title={disabled ? "This candidate cannot accept this seat." : ""}
                onClick={() => requestApply(programme)}
                disabled={disabled || isConfirming}
                style={{ width: "auto", backgroundColor: disabled || isConfirming ? '#91f8a9c9' : '#15c63ece', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: disabled || isConfirming ? 'default' : 'pointer' }}
            >
                Accept
            </button>
        )
    }

    const fetchApplicationByIIR = async (iir: number): Promise<number | null> => {
        setLoading(true);

        try {
            const driveId = fetchedDataById?.resource?.[0]?.drive_id;
            let args = `iir:${iir}`;
            if (driveId) {
                args += `,drive_id:${driveId}`;
            }
            if (selectedRound?.id) {
                args += `,round_id:${selectedRound.id}`;
            }
            const url = `${apiConfig.getResourceUrl(
                "applications"
            )}?queryId=GET_APPLICATION_BY_IIR&args=${encodeURIComponent(args)}`;

            const response = await apiFetch(url);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            const apps = Array.isArray(data)
                ? data
                : Array.isArray(data?.resource)
                    ? data.resource
                    : [];

            const appId = apps?.[0]?.id;

            if (appId) {
                return appId;
            }

            toast.info(`Application not found for token number ${iir}`);
            return null;
        } catch (err) {
            console.error("Unable to load application", err);
            toast.error("Unable to load application");
            return null;
        } finally {
            setLoading(false);
        }
    };

    const handleViewOnlineRound = async (prev: boolean, next: boolean) => {
        let appId
        let targetToken = token;
        if (prev && token) {
            targetToken = token - 1;
            appId = await fetchApplicationByIIR(targetToken);
        } if (next && token) {
            targetToken = token + 1;
            appId = await fetchApplicationByIIR(targetToken);
        }

        if (appId) {
            navigate(`/onlineRound/${appId}`, { state: { token: targetToken } });
            console.log("Application ID:", appId);
        }
    }



    function formatDate(dateString: string): string {
        if (!dateString) return "NA";
        const cleaned = dateString
            // Remove [Asia/Kolkata]
            .replace(/\[.*\]$/, "")
            // Keep only milliseconds (3 digits)
            .replace(/\.(\d{3})\d+/, ".$1");

        return moment.parseZone(cleaned).format("DD-MM-YYYY hh:mm A");
    }

    const handleSearch = async () => {
        const targetToken = parseInt(searchText);
        const appId = await fetchApplicationByIIR(targetToken);

        if (appId) {
            navigate(`/onlineRound/${appId}`, { state: { token: targetToken } });
            console.log("Application ID:", appId);
        }
    }
    return (
        <div className="student-home-container">
            {/* Header */}
            <header className="student-header">
                <div className="header-left">
                    <img src={iiitblogo} alt="IIITB" className="header-logo" />
                    <div className="header-title-group">
                        <h1 className="header-inst-name">
                            International Institute of Information Technology Bangalore
                        </h1>
                        <span className="header-portal-name">Admissions Portal</span>
                    </div>
                </div>
                <div className="header-right">
                    <div className="user-profile">
                        <div className="user-avatar" title={userEmail}>
                            <UserIcon size={20} />
                        </div>
                        <span className="user-email-text">{userEmail.split("@")[0]}</span>
                    </div>
                    {isAdmin ?
                        <button className="logout-btn" onClick={() => { navigate("/viewrounds/online") }}>
                            <LogOut size={16} />
                            <span>Go To Rounds</span>
                        </button>
                        :
                        <LogoutButton className="logout-btn" />
                    }
                </div>
            </header>

            <div style={{ padding: 24 }}>
                <h3 style={{ marginBottom: "15px", textAlign: "center", fontWeight: 600 }}>{selectedRound?.name} - Seat Allocation Status</h3>
                <h4 style={{ marginBottom: "15px", textAlign: "center", fontWeight: 600 }}>Drive: {drives.find(d => d.id === fetchedDataById?.resource?.[0]?.drive_id)?.drivetype === "Mtech" ? "M.Tech" : "B.Tech & Integrated M.Tech"}</h4>
                {isAdmin &&
                    <div style={{ marginLeft: "40px", marginRight: "40px" }}>
                        <div style={{ display: "flex", gap: 20, marginBottom: 20, justifyContent: "end", marginRight: 20 }}>
                            <input
                                type="text"
                                className="form-control bg-white shadow-sm"
                                placeholder="Enter Token Number"
                                value={searchText}
                                onChange={(e) => { setSearchText(e.target.value) }}
                                style={{
                                    padding: "6px 10px", borderRadius: "6px",
                                    border: "1px solid #d1d5db", fontSize: "13px",
                                    color: "#374151", backgroundColor: "#fff", flex: '0 1 250px'
                                }}
                            />
                            <button
                                className="btn btn-primary btn-sm fw-medium px-4 text-white border-0 shadow-sm me-2"
                                onClick={handleSearch}
                                style={{ borderRadius: "6px", height: "36px", backgroundColor: "#3b82f6" }}
                            >
                                Search
                            </button>
                        </div>
                        <div style={{ display: "flex", justifyContent: "space-between" }}>
                            <button
                                className="apply-btn"
                                onClick={() => handleViewOnlineRound(true, false)}
                                style={{ width: "auto", backgroundColor: '#15c63ece', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                            >
                                Previous Token
                            </button>
                            <button
                                className="apply-btn"
                                onClick={() => handleViewOnlineRound(false, true)}
                                style={{ width: "auto", backgroundColor: '#15c63ece', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', cursor: 'pointer' }}
                            >
                                Next Token
                            </button>
                        </div>
                    </div>
                }
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginLeft: 44, marginRight: 44, marginTop: 20, marginBottom: "15px" }}>
                    <div>
                        <h5 style={{ fontWeight: 600, margin: "0 0 4px 0" }}>Token Number - {""}
                            <span style={{ fontWeight: 600 }}>
                                {(() => {
                                    if (isMtech) {
                                        const tokens = [getOnlineField(appData, 'online_round_token'), getOnlineField(appData, 'online_round_token_2')].filter(Boolean);
                                        if (tokens.length > 0) {
                                            return tokens.map((t, idx) => (
                                                <span key={idx}>
                                                    <span style={Number(t) === token ? { backgroundColor: "#dbeafe", color: "#1e40af", padding: "2px 6px", borderRadius: "4px" } : {}}>
                                                        {t}
                                                    </span>
                                                    {idx < tokens.length - 1 ? ", " : ""}
                                                </span>
                                            ));
                                        }
                                    }
                                    return [getOnlineField(appData, 'online_round_token'), getOnlineField(appData, 'online_round_token_2')].filter(Boolean).join(", ") || token || iir || "NA";
                                })()}
                            </span>
                        </h5>
                        <h6 style={{ fontWeight: 500, margin: 0 }}>Name: {""}
                            <span style={{ fontWeight: 500 }}>
                                {name ?? "NA"}
                            </span>
                        </h6>
                    </div>
                    {appData.id && (
                        <div style={{
                            display: "flex",
                            gap: "16px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "8px",
                            padding: "10px 16px",
                            boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)"
                        }}>
                            {!isMtech && (
                                <div style={{ fontSize: "12px", borderRight: "1px solid #e2e8f0", paddingRight: "16px" }}>
                                    <span style={{ display: "block", color: "#64748b", fontWeight: 500, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.05em", marginBottom: "2px" }}>BJR</span>
                                    <strong style={{ color: "#0f172a", fontSize: "14px" }}>{appData.bjr ?? "NA"}</strong>
                                </div>
                            )}
                            <div style={{ fontSize: "12px", borderRight: "1px solid #e2e8f0", paddingRight: "16px" }}>
                                <span style={{ display: "block", color: "#64748b", fontWeight: 500, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.05em", marginBottom: "2px" }}>Payment Status</span>
                                <strong style={{ color: appData.offered_payment_status === "SUCCESS" ? "#16a34a" : "#dc2626", fontSize: "14px" }}>{appData.offered_payment_status ?? "NA"}</strong>
                            </div>
                            <div style={{ fontSize: "12px", borderRight: "1px solid #e2e8f0", paddingRight: "16px" }}>
                                <span style={{ display: "block", color: "#64748b", fontWeight: 500, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.05em", marginBottom: "2px" }}>Offered Programme</span>
                                <strong style={{ color: "#0f172a", fontSize: "14px" }}>{programmeFullNames[preRound2Programme as string] || preRound2Programme?.replace(/_/g, " ") || "NA"}</strong>
                            </div>
                            <div style={{ fontSize: "12px", borderRight: getOnlineField(appData, 'online_round_allocated_programme') ? "1px solid #e2e8f0" : "none", paddingRight: getOnlineField(appData, 'online_round_allocated_programme') ? "16px" : "0" }}>
                                <span style={{ display: "block", color: "#64748b", fontWeight: 500, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.05em", marginBottom: "2px" }}>Offer Status</span>
                                <strong style={{ color: "#2563eb", fontSize: "14px" }}>{appData.offer_status === "REJECTED" ? "DECLINED" : (appData.offer_status || "NA")}</strong>
                            </div>
                            {getOnlineField(appData, 'online_round_allocated_programme') && (
                                <div style={{ fontSize: "12px" }}>
                                    <span style={{ display: "block", color: "#64748b", fontWeight: 500, textTransform: "uppercase", fontSize: "10px", letterSpacing: "0.05em", marginBottom: "2px" }}>Online Allocated Programme</span>
                                    <strong style={{ color: "#1e3a8a", fontSize: "14px" }}>
                                        {[getAbbreviatedProg(getOnlineField(appData, 'online_round_allocated_programme')), getOnlineField(appData, 'online_round_allocated_programme_2') ? getAbbreviatedProg(getOnlineField(appData, 'online_round_allocated_programme_2')) : null].filter(Boolean).join(", ")}
                                    </strong>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {(!isMtech && disableApply && iir) && (
                    <div style={{ marginLeft: 44, marginRight: 24, padding: "12px 16px", backgroundColor: "#e0f2fe", borderLeft: "4px solid #0284c7", borderRadius: "4px", marginBottom: "10px" }}>
                        <h6 style={{ margin: 0, color: "#0369a1", fontWeight: 600 }}>
                            Note: This candidate has already been allocated a seat.
                        </h6>
                    </div>
                )}
                {(isMtech && iir && getOnlineField(appData, 'online_round_allocated_programme')) && (
                    <div style={{ marginLeft: 44, marginRight: 24, padding: "12px 16px", backgroundColor: "#e0f2fe", borderLeft: "4px solid #0284c7", borderRadius: "4px", marginBottom: "10px" }}>
                        <h6 style={{ margin: 0, color: "#0369a1", fontWeight: 600 }}>
                            Note: This candidate has been allocated a seat in {getAbbreviatedProg(getOnlineField(appData, 'online_round_allocated_programme'))}
                            {getOnlineField(appData, 'online_round_allocated_programme_2')
                                ? ` and ${getAbbreviatedProg(getOnlineField(appData, 'online_round_allocated_programme_2'))}.`
                                : "."}
                        </h6>
                    </div>
                )}

                {iir && <div style={{ padding: 24, paddingTop: 10 }}>
                    {analyticsLoading ? (
                        <div className="analytics-loading">Loading analytics...</div>
                    ) : analytics ? (
                        <div className="analytics-table-wrapper" style={{ marginBottom: "40px", overflow: "scroll" }}>
                            <table className="analytics-table">
                                <thead>
                                    <tr>
                                        <th>Programme</th>
                                        <th>Seats Available</th>
                                        <th>Accept Seat</th>
                                        <th>Last Updated</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="analytics-row-total">
                                        <td><strong>{programmeFullNames[analytics.programme1] || analytics.programme1}</strong></td>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                {isAdmin && (
                                                    <button
                                                        disabled={n(analytics.seatsavailable1) <= 0}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "28px",
                                                            height: "28px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: n(analytics.seatsavailable1) <= 0 ? "#e2e8f0" : (hoveredBtn === `${analytics.programme1}-minus` ? "#e2e8f0" : "#f1f5f9"),
                                                            color: n(analytics.seatsavailable1) <= 0 ? "#94a3b8" : "#334155",
                                                            fontSize: "16px",
                                                            fontWeight: "bold",
                                                            cursor: n(analytics.seatsavailable1) <= 0 ? "not-allowed" : "pointer",
                                                            transition: "all 0.15s ease",
                                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                            opacity: n(analytics.seatsavailable1) <= 0 ? 0.6 : 1,
                                                        }}
                                                        onMouseEnter={() => {
                                                            if (n(analytics.seatsavailable1) > 0) setHoveredBtn(`${analytics.programme1}-minus`);
                                                        }}
                                                        onMouseLeave={() => setHoveredBtn(null)}
                                                        onClick={() => triggerConfirmModal(analytics.programme1, -1)}
                                                    >-</button>
                                                )}
                                                <strong>{n(analytics.seatsavailable1)}</strong>
                                                {isAdmin && (
                                                    <button
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "28px",
                                                            height: "28px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: hoveredBtn === `${analytics.programme1}-plus` ? "#e2e8f0" : "#f1f5f9",
                                                            color: "#334155",
                                                            fontSize: "16px",
                                                            fontWeight: "bold",
                                                            cursor: "pointer",
                                                            transition: "all 0.15s ease",
                                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                        }}
                                                        onMouseEnter={() => setHoveredBtn(`${analytics.programme1}-plus`)}
                                                        onMouseLeave={() => setHoveredBtn(null)}
                                                        onClick={() => triggerConfirmModal(analytics.programme1, 1)}
                                                    >+</button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ display: "flex", justifyContent: "center" }}><ApplyButton programme={analytics.programme1} /></td>
                                        <td><strong>{formatDate(analytics.lastUpdated1)}</strong></td>
                                    </tr>
                                    <tr className="analytics-row-total">
                                        <td><strong>{programmeFullNames[analytics.programme2] || analytics.programme2}</strong></td>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                {isAdmin && (
                                                    <button
                                                        disabled={n(analytics.seatsavailable2) <= 0}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "28px",
                                                            height: "28px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: n(analytics.seatsavailable2) <= 0 ? "#e2e8f0" : (hoveredBtn === `${analytics.programme2}-minus` ? "#e2e8f0" : "#f1f5f9"),
                                                            color: n(analytics.seatsavailable2) <= 0 ? "#94a3b8" : "#334155",
                                                            fontSize: "16px",
                                                            fontWeight: "bold",
                                                            cursor: n(analytics.seatsavailable2) <= 0 ? "not-allowed" : "pointer",
                                                            transition: "all 0.15s ease",
                                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                            opacity: n(analytics.seatsavailable2) <= 0 ? 0.6 : 1,
                                                        }}
                                                        onMouseEnter={() => {
                                                            if (n(analytics.seatsavailable2) > 0) setHoveredBtn(`${analytics.programme2}-minus`);
                                                        }}
                                                        onMouseLeave={() => setHoveredBtn(null)}
                                                        onClick={() => triggerConfirmModal(analytics.programme2, -1)}
                                                    >-</button>
                                                )}
                                                <strong>{n(analytics.seatsavailable2)}</strong>
                                                {isAdmin && (
                                                    <button
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "28px",
                                                            height: "28px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: hoveredBtn === `${analytics.programme2}-plus` ? "#e2e8f0" : "#f1f5f9",
                                                            color: "#334155",
                                                            fontSize: "16px",
                                                            fontWeight: "bold",
                                                            cursor: "pointer",
                                                            transition: "all 0.15s ease",
                                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                        }}
                                                        onMouseEnter={() => setHoveredBtn(`${analytics.programme2}-plus`)}
                                                        onMouseLeave={() => setHoveredBtn(null)}
                                                        onClick={() => triggerConfirmModal(analytics.programme2, 1)}
                                                    >+</button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ display: "flex", justifyContent: "center" }}><ApplyButton programme={analytics.programme2} /></td>
                                        <td><strong>{formatDate(analytics.lastUpdated2)}</strong></td>
                                    </tr>
                                    <tr className="analytics-row-total">
                                        <td><strong>{programmeFullNames[analytics.programme3] || analytics.programme3}</strong></td>
                                        <td>
                                            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                {isAdmin && (
                                                    <button
                                                        disabled={n(analytics.seatsavailable3) <= 0}
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "28px",
                                                            height: "28px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: n(analytics.seatsavailable3) <= 0 ? "#e2e8f0" : (hoveredBtn === `${analytics.programme3}-minus` ? "#e2e8f0" : "#f1f5f9"),
                                                            color: n(analytics.seatsavailable3) <= 0 ? "#94a3b8" : "#334155",
                                                            fontSize: "16px",
                                                            fontWeight: "bold",
                                                            cursor: n(analytics.seatsavailable3) <= 0 ? "not-allowed" : "pointer",
                                                            transition: "all 0.15s ease",
                                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                            opacity: n(analytics.seatsavailable3) <= 0 ? 0.6 : 1,
                                                        }}
                                                        onMouseEnter={() => {
                                                            if (n(analytics.seatsavailable3) > 0) setHoveredBtn(`${analytics.programme3}-minus`);
                                                        }}
                                                        onMouseLeave={() => setHoveredBtn(null)}
                                                        onClick={() => triggerConfirmModal(analytics.programme3, -1)}
                                                    >-</button>
                                                )}
                                                <strong>{n(analytics.seatsavailable3)}</strong>
                                                {isAdmin && (
                                                    <button
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: "28px",
                                                            height: "28px",
                                                            borderRadius: "6px",
                                                            border: "1px solid #d1d5db",
                                                            backgroundColor: hoveredBtn === `${analytics.programme3}-plus` ? "#e2e8f0" : "#f1f5f9",
                                                            color: "#334155",
                                                            fontSize: "16px",
                                                            fontWeight: "bold",
                                                            cursor: "pointer",
                                                            transition: "all 0.15s ease",
                                                            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                        }}
                                                        onMouseEnter={() => setHoveredBtn(`${analytics.programme3}-plus`)}
                                                        onMouseLeave={() => setHoveredBtn(null)}
                                                        onClick={() => triggerConfirmModal(analytics.programme3, 1)}
                                                    >+</button>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ display: "flex", justifyContent: "center" }}><ApplyButton programme={analytics.programme3} /></td>
                                        <td><strong>{formatDate(analytics.lastUpdated3)}</strong></td>
                                    </tr>
                                    {analytics.programme4 && (
                                        <tr className="analytics-row-total">
                                            <td><strong>{programmeFullNames[analytics.programme4] || analytics.programme4}</strong></td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                    {isAdmin && (
                                                        <button
                                                            disabled={n(analytics.seatsavailable4) <= 0}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "6px",
                                                                border: "1px solid #d1d5db",
                                                                backgroundColor: n(analytics.seatsavailable4) <= 0 ? "#e2e8f0" : (hoveredBtn === `${analytics.programme4}-minus` ? "#e2e8f0" : "#f1f5f9"),
                                                                color: n(analytics.seatsavailable4) <= 0 ? "#94a3b8" : "#334155",
                                                                fontSize: "16px",
                                                                fontWeight: "bold",
                                                                cursor: n(analytics.seatsavailable4) <= 0 ? "not-allowed" : "pointer",
                                                                transition: "all 0.15s ease",
                                                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                                opacity: n(analytics.seatsavailable4) <= 0 ? 0.6 : 1,
                                                            }}
                                                            onMouseEnter={() => {
                                                                if (n(analytics.seatsavailable4) > 0) setHoveredBtn(`${analytics.programme4}-minus`);
                                                            }}
                                                            onMouseLeave={() => setHoveredBtn(null)}
                                                            onClick={() => triggerConfirmModal(analytics.programme4, -1)}
                                                        >-</button>
                                                    )}
                                                    <strong>{n(analytics.seatsavailable4)}</strong>
                                                    {isAdmin && (
                                                        <button
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "6px",
                                                                border: "1px solid #d1d5db",
                                                                backgroundColor: hoveredBtn === `${analytics.programme4}-plus` ? "#e2e8f0" : "#f1f5f9",
                                                                color: "#334155",
                                                                fontSize: "16px",
                                                                fontWeight: "bold",
                                                                cursor: "pointer",
                                                                transition: "all 0.15s ease",
                                                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                            }}
                                                            onMouseEnter={() => setHoveredBtn(`${analytics.programme4}-plus`)}
                                                            onMouseLeave={() => setHoveredBtn(null)}
                                                            onClick={() => triggerConfirmModal(analytics.programme4, 1)}
                                                        >+</button>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ display: "flex", justifyContent: "center" }}><ApplyButton programme={analytics.programme4} /></td>
                                            <td><strong>{formatDate(analytics.lastUpdated4)}</strong></td>
                                        </tr>
                                    )}
                                    {analytics.programme5 && (
                                        <tr className="analytics-row-total">
                                            <td><strong>{programmeFullNames[analytics.programme5] || analytics.programme5}</strong></td>
                                            <td>
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                                                    {isAdmin && (
                                                        <button
                                                            disabled={n(analytics.seatsavailable5) <= 0}
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "6px",
                                                                border: "1px solid #d1d5db",
                                                                backgroundColor: n(analytics.seatsavailable5) <= 0 ? "#e2e8f0" : (hoveredBtn === `${analytics.programme5}-minus` ? "#e2e8f0" : "#f1f5f9"),
                                                                color: n(analytics.seatsavailable5) <= 0 ? "#94a3b8" : "#334155",
                                                                fontSize: "16px",
                                                                fontWeight: "bold",
                                                                cursor: n(analytics.seatsavailable5) <= 0 ? "not-allowed" : "pointer",
                                                                transition: "all 0.15s ease",
                                                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                                opacity: n(analytics.seatsavailable5) <= 0 ? 0.6 : 1,
                                                            }}
                                                            onMouseEnter={() => {
                                                                if (n(analytics.seatsavailable5) > 0) setHoveredBtn(`${analytics.programme5}-minus`);
                                                            }}
                                                            onMouseLeave={() => setHoveredBtn(null)}
                                                            onClick={() => triggerConfirmModal(analytics.programme5, -1)}
                                                        >-</button>
                                                    )}
                                                    <strong>{n(analytics.seatsavailable5)}</strong>
                                                    {isAdmin && (
                                                        <button
                                                            style={{
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                width: "28px",
                                                                height: "28px",
                                                                borderRadius: "6px",
                                                                border: "1px solid #d1d5db",
                                                                backgroundColor: hoveredBtn === `${analytics.programme5}-plus` ? "#e2e8f0" : "#f1f5f9",
                                                                color: "#334155",
                                                                fontSize: "16px",
                                                                fontWeight: "bold",
                                                                cursor: "pointer",
                                                                transition: "all 0.15s ease",
                                                                boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
                                                            }}
                                                            onMouseEnter={() => setHoveredBtn(`${analytics.programme5}-plus`)}
                                                            onMouseLeave={() => setHoveredBtn(null)}
                                                            onClick={() => triggerConfirmModal(analytics.programme5, 1)}
                                                        >+</button>
                                                    )}
                                                </div>
                                            </td>
                                            <td style={{ display: "flex", justifyContent: "center" }}><ApplyButton programme={analytics.programme5} /></td>
                                            <td><strong>{formatDate(analytics.lastUpdated5)}</strong></td>
                                        </tr>
                                    )}

                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="analytics-loading">No data available.</div>
                    )}

                    {fetchedDataById?.resource?.[0] && (
                        <div style={{ marginTop: "40px" }}>
                            <h4 style={{ marginBottom: "15px", fontWeight: 600 }}>Candidate Full Details</h4>
                            <div style={{ backgroundColor: "#f9fafb", padding: "20px", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
                                    <h5 style={{ fontWeight: 600, margin: 0, color: "#111827" }}>Application Number: {""}
                                        <span style={{ color: "#2563eb" }}>
                                            {appData.application_number ?? "NA"}
                                        </span>
                                    </h5>
                                </div>

                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
                                    {/* Left Column */}
                                    <div>
                                        {renderDetailSection("Personal Details", [
                                            { label: "Full Name", value: appData.fullname },
                                            { label: "Email", value: appData.email },
                                            { label: "Mobile", value: appData.mobile },
                                            { label: "Date of Birth", value: appData.dob },
                                            { label: "Gender", value: appData.gender },
                                            { label: "Nationality", value: appData.nationality },
                                            { label: "ABC ID", value: appData.abc_id },
                                        ])}

                                        {renderDetailSection("Parent/Guardian Details", [
                                            { label: "Name", value: appData.guardian_name },
                                            { label: "Relation", value: appData.guardian_relation },
                                            { label: "Mobile", value: appData.guardian_mobile },
                                            { label: "Email", value: appData.guardian_email },
                                            { label: "Alternate Mobile", value: appData.alternatemobile },
                                        ])}

                                        {renderDetailSection("Application Payment Details", [
                                            { label: "Transaction ID", value: appData.transaction_id },
                                            { label: "Amount", value: appData.amount },
                                            { label: "Status", value: appData.payment_status?.replace(/_/g, " ") },
                                            { label: "Date", value: formatDate(appData.payment_date) },
                                            { label: "Mode", value: appData.payment_gateway },
                                        ])}
                                    </div>

                                    {/* Right Column */}
                                    <div>
                                        {renderDetailSection("Current Address", [
                                            { label: "Address Line 1", value: appData.line1 },
                                            { label: "Address Line 2", value: appData.line2 },
                                            { label: "City", value: appData.city },
                                            { label: "State", value: appData.state },
                                            { label: "Pincode", value: appData.pincode },
                                            { label: "Country", value: appData.country },
                                        ])}

                                        {renderDetailSection("Permanent Address", [
                                            { label: "Address Line 1", value: appData.p_line1 },
                                            { label: "Address Line 2", value: appData.p_line2 },
                                            { label: "City", value: appData.p_city },
                                            { label: "State", value: appData.p_state },
                                            { label: "Pincode", value: appData.p_pincode },
                                            { label: "Country", value: appData.p_country },
                                        ])}

                                        {isMtech && renderDetailSection("Graduation Details", [
                                            { label: "College Name", value: appData.collegename },
                                            { label: "Degree", value: appData.degree },
                                            { label: "Major", value: appData.major },
                                            { label: "Percentage", value: appData.cgpa },
                                            { label: "CGPA/Marks Obtained", value: appData.marks_obtained },
                                            { label: "Max Marks/CGPA", value: appData.maximum_marks },
                                            { label: "Year of Passing", value: appData.passingyear },
                                        ])}

                                        {!isMtech && renderDetailSection("Education & JEE Details", [
                                            { label: "10th Percentage", value: appData.edu10_percentage },
                                            { label: "12th Percentage", value: appData.edu12_percentage },
                                            { label: "12th Maths %", value: appData.edu12_percentagemaths },
                                            { label: "JEE Main Score", value: appData.jeemain_score },
                                            { label: "JEE Main Rank", value: appData.jeemain_rank },
                                            { label: "JEE Main Roll No", value: appData.jeemain_rollno },
                                            { label: "JEE Advanced Score", value: appData.jeeadv_score },
                                            { label: "JEE Advanced Rank", value: appData.jeeadv_rank },
                                            { label: "JEE Advanced Roll No", value: appData.jeeadv_rollno },
                                        ])}

                                        {isMtech && mtechPrefs.map((p, index) => {
                                            const normProg = p.prog?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                                            const isRejected = appData.admin_rejected_programmes &&
                                                appData.admin_rejected_programmes.toLowerCase().replace(/[^a-zA-Z0-9,]/g, "").split(",").includes(normProg);
                                            const headerLabel = `GATE Details - ${p.prog?.replace(/_/g, " ") || ""}`;

                                            return (
                                                <div key={index}>
                                                    {renderDetailSection(headerLabel, [
                                                        { label: "GATE Subjects", value: p.subjects },
                                                        { label: "GATE Score", value: p.score },
                                                        { label: "GATE Rank", value: p.rank },
                                                        { label: "GATE Roll No", value: p.rollno },
                                                        { label: "GATE Year", value: p.year },
                                                    ], isRejected)}
                                                </div>
                                            );
                                        })}

                                        {renderDetailSection("Offer Payment Details (if any)", [
                                            { label: "Round Name", value: latestOfferedRoundName },
                                            { label: "Offer Status", value: appData.offer_status === "REJECTED" ? "DECLINED" : appData.offer_status },
                                            { label: "Sliding Status", value: appData.online_round5_sliding_status || appData.online_round4_sliding_status || appData.online_round3_sliding_status || appData.online_round2_sliding_status || appData.online_sliding_status || appData.current_sliding_status || appData.prev_sliding_status },
                                            { label: "Offered Programme", value: preRound2Programme?.replace(/_/g, " ") },
                                            { label: "Payment Status", value: (appData.offered_payment_status || appData.extra_data?.payment_status)?.replace(/_/g, " ") },
                                            { label: "Payment Amount", value: appData.offered_round_amount || appData.extra_data?.payment_amount },
                                            { label: "Payment Date", value: formatDate(appData.offered_payment_date) || formatDate(appData.extra_data?.payment_date) },
                                        ])}

                                        {!isMtech && renderDetailSection("Programme Preferences", [
                                            { label: "Preference 1", value: appData.pref1 },
                                            { label: "Preference 2", value: appData.pref2 },
                                            { label: "Preference 3", value: appData.pref3 },
                                            { label: "Preference 4", value: appData.pref4 },
                                            { label: "Preference 5", value: appData.pref5 },
                                        ])}
                                    </div>
                                </div>

                                {!isMtech && (
                                    <div style={{ marginTop: 10, padding: 12, backgroundColor: "#fff", border: "1px solid #e5e7eb", borderRadius: 6 }}>
                                        <h6 style={{ fontWeight: 600, color: "#374151", margin: 0 }}>Best JEE Rank: <span style={{ color: "#111827" }}>{bjr ?? "NA"}</span></h6>
                                    </div>
                                )}

                                {isMtech && (
                                    <div style={{ marginTop: 20 }}>
                                        <h6 style={{ fontWeight: 600, borderBottom: "1px solid #e5e7eb", paddingBottom: 4, marginBottom: 8, color: "#374151" }}>GATE Details & Programme Preferences</h6>
                                        {mtechPrefs.length > 0 ? (
                                            <table style={{ width: "100%", marginTop: 8, borderCollapse: "collapse", fontSize: 13, backgroundColor: "#fff" }}>
                                                <thead>
                                                    <tr style={{ backgroundColor: "#f3f4f6" }}>
                                                        {/* <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>Programme</th> */}
                                                        <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>Programme Applied</th>
                                                        <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>GATE Subjects</th>
                                                        <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>GATE Score</th>
                                                        {/* <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>GATE Rank</th> */}
                                                        <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>GATE Year</th>
                                                        <th style={{ border: "1px solid #d1d5db", padding: "8px 10px", textAlign: "left" }}>Qualified Programme</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {mtechPrefs.map((p, i) => {
                                                        const normProg = p.prog?.replace(/[^a-zA-Z0-9]/g, "").toLowerCase();
                                                        const isQualified = normProg && invitedProgrammes.includes(normProg);
                                                        return (
                                                            <tr key={i}>
                                                                <td style={{ border: "1px solid #d1d5db", padding: "6px 10px" }}>{p.prog?.replace(/_/g, " ") ?? "—"}</td>
                                                                <td style={{ border: "1px solid #d1d5db", padding: "6px 10px" }}>{p.subjects ?? "—"}</td>
                                                                <td style={{ border: "1px solid #d1d5db", padding: "6px 10px", fontWeight: 500, color: "#2563eb" }}>{p.score ?? "—"}</td>
                                                                {/* <td style={{ border: "1px solid #d1d5db", padding: "6px 10px" }}>{p.rank ?? "—"}</td> */}
                                                                <td style={{ border: "1px solid #d1d5db", padding: "6px 10px" }}>{p.year ?? "—"}</td>
                                                                <td style={{ border: "1px solid #d1d5db", padding: "6px 10px", fontWeight: 600, color: isQualified ? "#15803d" : "#dc2626" }}>
                                                                    {isQualified ? "QUALIFIED" : "NOT QUALIFIED"}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        ) : (
                                            <p style={{ fontSize: 13, color: "#6b7280" }}>No programme preferences found.</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>}
            </div>

            {confirmModalData.isOpen && (
                <div style={{
                    position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1000,
                    display: "flex", justifyContent: "center", alignItems: "center"
                }}>
                    <div style={{
                        backgroundColor: "#fff", padding: "24px", borderRadius: "8px",
                        width: "100%", maxWidth: "400px", boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
                    }}>
                        <h4 style={{ marginTop: 0, fontWeight: 600, fontSize: "18px", color: "#111827" }}>Confirm Allocation</h4>
                        <p style={{ color: "#374151", fontSize: "14px", marginTop: "12px", marginBottom: "20px" }}>
                            Are you sure you want to allocate <strong>{confirmModalData.programme.replace(/_/g, " ")}</strong> to this candidate? This action will allocate the seat and send an allocation email.
                        </p>
                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                            <button
                                onClick={() => setConfirmModalData({ isOpen: false, programme: "" })}
                                style={{ padding: "8px 16px", border: "1px solid #d1d5db", backgroundColor: "#fff", color: "#374151", borderRadius: "6px", cursor: "pointer", fontWeight: 500 }}
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    handleApply(confirmModalData.programme);
                                    setConfirmModalData({ isOpen: false, programme: "" });
                                }}
                                style={{ padding: "8px 16px", border: "none", backgroundColor: "#2563eb", color: "#fff", borderRadius: "6px", cursor: "pointer", fontWeight: 500 }}
                            >
                                Allocate
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {confirmModal.isOpen && (
                <div style={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: "rgba(15, 23, 42, 0.6)",
                    backdropFilter: "blur(4px)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    zIndex: 9999,
                }}>
                    <div style={{
                        backgroundColor: "#ffffff",
                        borderRadius: "12px",
                        padding: "24px",
                        width: "100%",
                        maxWidth: "400px",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        border: "1px solid #e2e8f0",
                        textAlign: "center"
                    }}>
                        <div style={{
                            width: "48px",
                            height: "48px",
                            borderRadius: "50%",
                            backgroundColor: confirmModal.delta > 0 ? "#ecfdf5" : "#fef2f2",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto 16px auto"
                        }}>
                            {confirmModal.delta > 0 ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#ef4444" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                            )}
                        </div>
                        <h3 style={{
                            fontSize: "18px",
                            fontWeight: 600,
                            color: "#1e293b",
                            marginBottom: "8px"
                        }}>
                            Confirm Seat Count Change
                        </h3>
                        <p style={{
                            fontSize: "14px",
                            color: "#64748b",
                            marginBottom: "24px",
                            lineHeight: "1.5"
                        }}>
                            Are you sure you want to <strong>{confirmModal.delta > 0 ? "increase" : "decrease"}</strong> the remaining seats for <strong>{programmeFullNames[confirmModal.programme] || confirmModal.programme}</strong> by 1?
                        </p>
                        <div style={{
                            display: "flex",
                            justifyContent: "center",
                            gap: "12px"
                        }}>
                            <button
                                style={{
                                    padding: "10px 16px",
                                    borderRadius: "6px",
                                    border: "1px solid #cbd5e1",
                                    backgroundColor: "#ffffff",
                                    color: "#334155",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                }}
                                onClick={() => setConfirmModal({ isOpen: false, programme: "", delta: 0 })}
                            >
                                Cancel
                            </button>
                            <button
                                style={{
                                    padding: "10px 16px",
                                    borderRadius: "6px",
                                    border: "none",
                                    backgroundColor: confirmModal.delta > 0 ? "#10b981" : "#ef4444",
                                    color: "#ffffff",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    transition: "all 0.15s ease",
                                    boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)"
                                }}
                                onClick={() => {
                                    handleUpdateSeats(confirmModal.programme, confirmModal.delta);
                                    setConfirmModal({ isOpen: false, programme: "", delta: 0 });
                                }}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default BtechOnlineRound
